import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '@config/AppConfigContext';
import { fetchJson, getAuthHeader, joinUrl } from '@hooks/requests/utils/_partner_api_client';
import { PARTNER_TEAM_PATH, PARTNER_TEAM_INVITATIONS_PATH } from '@hooks/requests/utils/paths';

export interface InviteMemberParams {
  email: string;
  role: string;
}

/**
 * `alreadyMember`/`resent` distinguish FR-006/FR-005 from a fresh invite
 * (FR-004) — the caller picks the toast copy from these, not from the HTTP
 * status alone (both a fresh invite and a resend answer 201).
 */
export interface InviteMemberResponse {
  invitationId?: string;
  email?: string;
  createdAt?: string | null;
  resent?: boolean;
  alreadyMember?: boolean;
}

export const useInviteMemberMutation = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, role }: InviteMemberParams): Promise<InviteMemberResponse> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');
      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);

      return fetchJson<InviteMemberResponse>(
        joinUrl(allyApi, PARTNER_TEAM_PATH),
        authHeader,
        'Failed to send the invitation',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, role }),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_TEAM_PATH] });
      queryClient.invalidateQueries({ queryKey: [PARTNER_TEAM_INVITATIONS_PATH] });
    },
  });
};

export default useInviteMemberMutation;
